import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  compareHandoffV1RunsCompareHandoffGet,
  compareStoreDiffV1RunsCompareStoreDiffGet,
  runStoreHullsV1RunsRunIdStoreHullsGet,
} from "../api/generated/qcsControlPlane";
import type { RunStoreHulls, StoreHull, StoreProbe } from "../api/generated/model";
import { fmtInt } from "../api/labels";
import { Badge, Button, Panel } from "@akaike/ui";

// Two store-footprint maps side by side with synchronized pan/zoom, the store-level
// half of the day-vs-night comparison, as an interactive DIFF. Each store this run
// found serviceable is drawn as its serviceable-area hull (rebuilt server-side from the
// run's coverage cells), coloured by how the two runs differ:
//   • removed: in A, gone in B   (red)
//   • added: new in B          (green)
//   • common: in both           (grey)
// Each map shows its own stores solid plus the OTHER run's exclusive stores as dashed
// "ghosts", so you see the full delta on either side. Click any store to select it (and
// fly both maps to it); filter chips isolate added / removed / common.

const INDIA: L.LatLngTuple = [22.6, 80.0];
const ADDED = "#2ea043";
const REMOVED = "#f85149";
const COMMON = "#8b949e";
const SELECTED = "#d29922";

type Cat = "added" | "removed" | "common";
type Filter = "all" | Cat;
const CAT_COLOR: Record<Cat, string> = {
  added: ADDED,
  removed: REMOVED,
  common: COMMON,
};

function useStoreMap() {
  return {
    elRef: useRef<HTMLDivElement | null>(null),
    mapRef: useRef<L.Map | null>(null),
    layerRef: useRef<L.LayerGroup | null>(null),
    hlRef: useRef<L.LayerGroup | null>(null), // selection highlight (covering stores)
  };
}
type MapHandle = ReturnType<typeof useStoreMap>;

// Distinct, stable colours for the covering stores of a selected store.
const PALETTE = [
  "#e6550d",
  "#3182bd",
  "#31a354",
  "#756bb1",
  "#d6616b",
  "#637939",
  "#8c6d31",
  "#9c27b0",
];
const paletteColor = (i: number) => PALETTE[i % PALETTE.length];

type Diff = {
  aById: Map<string, StoreHull>;
  bById: Map<string, StoreHull>;
  addedIds: Set<string>;
  removedIds: Set<string>;
  commonIds: Set<string>;
};

type Verdict = { tag: string; tone: "crit" | "warn" | "idle"; detail: string };

// Turn the other run's probe coverage of a changed store's footprint into a verdict.
function interpret(cat: Cat, probe: StoreProbe | undefined): Verdict | null {
  if (cat === "common") return null;
  const other = cat === "removed" ? "B" : "A";
  if (!probe || probe.probe_pts === 0)
    return {
      tag: "not scanned",
      tone: "idle",
      detail: `run ${other} didn't probe this area: inconclusive (the store may still serve)`,
    };
  if (probe.serv_pts === 0)
    return {
      tag: "area dark",
      tone: "crit",
      detail: `probed in ${other}, nothing serviceable there: genuinely not serviceable`,
    };
  return {
    tag: "area still served",
    tone: "warn",
    detail: `probed & serviceable in ${other} (other stores), this store specifically dropped`,
  };
}

function popupHtml(s: StoreHull, cat: Cat, probe: StoreProbe | undefined): string {
  const label = cat === "added" ? "added in B" : cat === "removed" ? "removed in B" : "in both";
  const name = s.name ? `<br/>${s.name}` : "";
  const v = interpret(cat, probe);
  const verdict = v ? `<br/><i>${v.tag}</i>, ${v.detail}` : "";
  return `<b>${s.store_id}</b> · ${label}${name}<br/>${s.cells} cells${verdict}`;
}

// Draw one panel: its own stores (solid) + the other run's exclusive stores (ghost).
function drawSide(
  h: MapHandle,
  own: StoreHull[],
  ownCat: (id: string) => Cat,
  ghost: StoreHull[],
  ghostCat: Cat,
  filter: Filter,
  selectedId: string | null,
  onSelect: (s: StoreHull) => void,
  probeFor: (cat: Cat, id: string) => StoreProbe | undefined,
) {
  const layer = h.layerRef.current;
  if (!layer) return;
  layer.clearLayers();
  const render = (s: StoreHull, cat: Cat, isGhost: boolean) => {
    if (filter !== "all" && cat !== filter) return;
    const color = CAT_COLOR[cat];
    const selected = s.store_id === selectedId;
    if (s.boundary && s.boundary.length >= 3) {
      L.polygon(s.boundary as [number, number][], {
        color: selected ? SELECTED : color,
        weight: selected ? 3 : isGhost ? 1 : 1.3,
        dashArray: isGhost ? "4 4" : undefined,
        fill: true,
        fillColor: color,
        fillOpacity: isGhost ? 0.04 : selected ? 0.4 : 0.18,
      })
        .on("click", () => onSelect(s))
        .addTo(layer);
    }
    L.circleMarker([s.lat, s.lon], {
      radius: selected ? 6 : 4,
      color: selected ? SELECTED : color,
      weight: selected ? 2.5 : 1.5,
      fillColor: color,
      fillOpacity: isGhost ? 0.45 : 0.9,
    })
      .on("click", () => onSelect(s))
      .bindPopup(popupHtml(s, cat, probeFor(cat, s.store_id)))
      .addTo(layer);
  };
  for (const s of own) render(s, ownCat(s.store_id), false);
  // Keep every ghost (incl. the selected store's red dashed outline, where it used to
  // serve); the covering-store highlight is drawn on top in its own colours.
  for (const s of ghost) render(s, ghostCat, true);
}

export function SyncedStoreMaps({
  runIdA,
  runIdB,
  labelA,
  labelB,
  focus,
}: {
  runIdA: string;
  runIdB: string;
  labelA: string;
  labelB: string;
  focus?: { id: string; n: number } | null; // external "locate" trigger (n forces re-fire)
}) {
  const a = useStoreMap();
  const b = useStoreMap();
  const syncingRef = useRef(false);
  const fittedRef = useRef(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<StoreHull | null>(null);

  const hullsA = useQuery({
    queryKey: ["store-hulls", runIdA],
    queryFn: () => runStoreHullsV1RunsRunIdStoreHullsGet(runIdA),
  });
  const hullsB = useQuery({
    queryKey: ["store-hulls", runIdB],
    queryFn: () => runStoreHullsV1RunsRunIdStoreHullsGet(runIdB),
  });
  // Was each changed store's footprint actually probed by the other run?
  const probeQ = useQuery({
    queryKey: ["store-diff-probe", runIdA, runIdB],
    queryFn: () =>
      compareStoreDiffV1RunsCompareStoreDiffGet({
        run_a: runIdA,
        run_b: runIdB,
      }),
  });
  const probe = useMemo(() => {
    const removed = new Map((probeQ.data?.removed ?? []).map((p) => [p.store_id, p]));
    const added = new Map((probeQ.data?.added ?? []).map((p) => [p.store_id, p]));
    return { removed, added };
  }, [probeQ.data]);
  const probeFor = (cat: Cat, id: string): StoreProbe | undefined =>
    cat === "removed" ? probe.removed.get(id) : cat === "added" ? probe.added.get(id) : undefined;
  // Handoff: which run-B store(s) cover a selected removed/shrank store's lost area.
  const handoffQ = useQuery({
    queryKey: ["store-handoff", runIdA, runIdB],
    queryFn: () => compareHandoffV1RunsCompareHandoffGet({ run_a: runIdA, run_b: runIdB }),
  });
  const handoff = useMemo(() => {
    const removed = new Map((handoffQ.data?.removed ?? []).map((h) => [h.store_id, h]));
    const shrank = new Map((handoffQ.data?.shrank ?? []).map((h) => [h.store_id, h]));
    return { removed, shrank };
  }, [handoffQ.data]);
  const bById = useMemo(
    () => new Map((hullsB.data?.stores ?? []).map((s) => [s.store_id, s])),
    [hullsB.data],
  );

  // Diff the two runs' store sets by store_id.
  const diff = useMemo<Diff>(() => {
    const aById = new Map((hullsA.data?.stores ?? []).map((s) => [s.store_id, s]));
    const bById = new Map((hullsB.data?.stores ?? []).map((s) => [s.store_id, s]));
    const addedIds = new Set<string>();
    const removedIds = new Set<string>();
    const commonIds = new Set<string>();
    for (const id of aById.keys()) (bById.has(id) ? commonIds : removedIds).add(id);
    for (const id of bById.keys()) if (!aById.has(id)) addedIds.add(id);
    return { aById, bById, addedIds, removedIds, commonIds };
  }, [hullsA.data, hullsB.data]);

  // Select a store + fly both maps to it (map A drives, sync mirrors to B).
  const selectStore = (s: StoreHull) => {
    setSelected(s);
    const map = a.mapRef.current;
    if (map)
      map.setView([s.lat, s.lon], Math.max(map.getZoom(), 14), {
        animate: true,
      });
  };

  // -- create both maps once + wire bidirectional pan/zoom sync ------------------
  useEffect(() => {
    if (!a.elRef.current || !b.elRef.current || a.mapRef.current) return;
    const mk = (h: MapHandle) => {
      const map = L.map(h.elRef.current as HTMLElement, {
        attributionControl: false,
        preferCanvas: true,
        worldCopyJump: true,
      }).setView(INDIA, 5);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
      }).addTo(map);
      h.mapRef.current = map;
      h.layerRef.current = L.layerGroup().addTo(map);
      h.hlRef.current = L.layerGroup().addTo(map); // on top of the base layer
      return map;
    };
    const mapA = mk(a);
    const mapB = mk(b);
    const mirror = (src: L.Map, dst: L.Map) =>
      src.on("move zoom", () => {
        if (syncingRef.current) return;
        syncingRef.current = true;
        dst.setView(src.getCenter(), src.getZoom(), { animate: false });
        syncingRef.current = false;
      });
    mirror(mapA, mapB);
    mirror(mapB, mapA);
    setTimeout(() => {
      mapA.invalidateSize();
      mapB.invalidateSize();
    }, 0);
    return () => {
      mapA.remove();
      mapB.remove();
      a.mapRef.current = null;
      b.mapRef.current = null;
      fittedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- external locate (from the changes table): select + fly + un-filter --------
  useEffect(() => {
    if (!focus) return;
    const s =
      (hullsA.data?.stores ?? []).find((x) => x.store_id === focus.id) ??
      (hullsB.data?.stores ?? []).find((x) => x.store_id === focus.id);
    if (s) {
      setFilter("all");
      selectStore(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  // -- fit once to the union of both runs' store footprints ----------------------
  useEffect(() => {
    const mapA = a.mapRef.current;
    if (!mapA || fittedRef.current) return;
    const pts: L.LatLngTuple[] = [];
    for (const d of [hullsA.data, hullsB.data]) {
      for (const s of d?.stores ?? []) pts.push([s.lat, s.lon]);
    }
    if (pts.length === 0) return;
    fittedRef.current = true;
    mapA.fitBounds(L.latLngBounds(pts).pad(0.1), { maxZoom: 13 });
  }, [hullsA.data, hullsB.data, a.mapRef]);

  // -- redraw both panels on data / filter / selection change --------------------
  useEffect(() => {
    const selId = selected?.store_id ?? null;
    drawSide(
      a,
      hullsA.data?.stores ?? [],
      (id) => (diff.removedIds.has(id) ? "removed" : "common"),
      (hullsB.data?.stores ?? []).filter((s) => diff.addedIds.has(s.store_id)),
      "added",
      filter,
      selId,
      selectStore,
      probeFor,
    );
    drawSide(
      b,
      hullsB.data?.stores ?? [],
      (id) => (diff.addedIds.has(id) ? "added" : "common"),
      (hullsA.data?.stores ?? []).filter((s) => diff.removedIds.has(s.store_id)),
      "removed",
      filter,
      selId,
      selectStore,
      probeFor,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hullsA.data, hullsB.data, diff, filter, selected, probe]);

  // -- highlight the covering stores of a selected removed/shrank store on map B --
  useEffect(() => {
    const hlA = a.hlRef.current;
    const hlB = b.hlRef.current;
    hlA?.clearLayers();
    hlB?.clearLayers();
    if (!selected || !hlB) return;
    const h = handoff.removed.get(selected.store_id) ?? handoff.shrank.get(selected.store_id);
    if (!h) return;
    h.covered_by.forEach((c, i) => {
      const store = bById.get(c.store_id);
      if (!store) return;
      const color = paletteColor(i);
      if (store.boundary && store.boundary.length >= 3) {
        L.polygon(store.boundary as [number, number][], {
          color,
          weight: 2.5,
          fillColor: color,
          fillOpacity: 0.5,
        }).addTo(hlB);
      }
      L.circleMarker([store.lat, store.lon], {
        radius: 6,
        color: "#fff",
        weight: 2,
        fillColor: color,
        fillOpacity: 1,
      })
        .bindTooltip(`${c.store_id} · +${c.cells} cells`, {
          permanent: true,
          direction: "top",
          opacity: 0.95,
        })
        .addTo(hlB);
    });
  }, [selected, handoff, bById, a.hlRef, b.hlRef]);

  const counts = {
    added: diff.addedIds.size,
    removed: diff.removedIds.size,
    common: diff.commonIds.size,
  };
  // Of the removed stores, how many did run B actually probe (-> confirmed) vs not (-> inconclusive)?
  const removedProbed = (probeQ.data?.removed ?? []).filter((p) => p.probe_pts > 0).length;
  const removedUnscanned = counts.removed - removedProbed;
  const loading = hullsA.isLoading || hullsB.isLoading;
  const error = hullsA.isError || hullsB.isError;

  return (
    <Panel style={{ gridColumn: "span 12", marginTop: 14 }}>
      <Panel.Head style={{ flexWrap: "wrap", gap: 8 }} title="Store serviceable areas · diff · synced pan &amp; zoom">
        <div className="flex-1" />
        <FilterChips filter={filter} setFilter={setFilter} counts={counts} />
      </Panel.Head>
      <Panel.Body tight>
        <SummaryBar
          counts={counts}
          loading={loading}
          removedProbed={removedProbed}
          removedUnscanned={removedUnscanned}
          probeReady={!probeQ.isLoading && !probeQ.isError}
        />
        {probeQ.data?.capped && (
          <div className="muted" style={{ fontSize: 11, padding: "0 4px 8px" }}>
            Large comparison: added/removed verdicts limited to the top stores.
          </div>
        )}
        {selected &&
          (() => {
            const cat: Cat = diff.removedIds.has(selected.store_id)
              ? "removed"
              : diff.addedIds.has(selected.store_id)
                ? "added"
                : "common";
            return (
              <SelectedBar
                s={selected}
                cat={cat}
                verdict={interpret(cat, probeFor(cat, selected.store_id))}
                loading={probeQ.isLoading}
                onClear={() => setSelected(null)}
              />
            );
          })()}
        <div className="row" style={{ gap: 10, alignItems: "stretch", flexWrap: "wrap" }}>
          <StorePanel handle={a} label={labelA} data={hullsA.data} error={hullsA.isError} />
          <StorePanel handle={b} label={labelB} data={hullsB.data} error={hullsB.isError} />
        </div>
        {error && (
          <div className="muted" style={{ fontSize: 12, padding: "8px 4px" }}>
            Could not load store hulls (is ClickHouse configured?).
          </div>
        )}
      </Panel.Body>
    </Panel>
  );
}

function FilterChips({
  filter,
  setFilter,
  counts,
}: {
  filter: Filter;
  setFilter: (f: Filter) => void;
  counts: { added: number; removed: number; common: number };
}) {
  const chip = (f: Filter, label: string, color?: string) => (
    <Button
      size="sm"
      variant={filter === f ? "default" : "outline"}
      style={{
        fontSize: 11,
        padding: "4px 9px",
        borderColor: color,
        color: filter === f ? undefined : color,
      }}
      onClick={() => setFilter(f)}
    >
      {label}
    </Button>
  );
  return (
    <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
      {chip("all", "all")}
      {chip("added", `+${counts.added} added`, ADDED)}
      {chip("removed", `−${counts.removed} removed`, REMOVED)}
      {chip("common", `${counts.common} common`, COMMON)}
    </div>
  );
}

function SummaryBar({
  counts,
  loading,
  removedProbed,
  removedUnscanned,
  probeReady,
}: {
  counts: { added: number; removed: number; common: number };
  loading: boolean;
  removedProbed: number;
  removedUnscanned: number;
  probeReady: boolean;
}) {
  const net = counts.added - counts.removed;
  return (
    <div className="col" style={{ gap: 4, padding: "2px 4px 10px" }}>
      <div className="row" style={{ gap: 16, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ color: ADDED, fontWeight: 600 }} className="num">
          +{fmtInt(counts.added)} added
        </span>
        <span style={{ color: REMOVED, fontWeight: 600 }} className="num">
          −{fmtInt(counts.removed)} removed
        </span>
        <span className="faint num">{fmtInt(counts.common)} unchanged</span>
        <span className="faint num">
          net {net >= 0 ? "+" : ""}
          {fmtInt(net)}
        </span>
        {loading && <span className="muted" style={{ fontSize: 11 }}>loading…</span>}
        <span className="faint" style={{ fontSize: 11 }}>
          click a removed store → its covering stores show by id &amp; colour on the B map
        </span>
      </div>
      {probeReady && counts.removed > 0 && (
        <div className="row" style={{ gap: 14, alignItems: "baseline", flexWrap: "wrap" }}>
          <span className="faint" style={{ fontSize: 11 }}>of removed:</span>
          <span style={{ color: REMOVED, fontSize: 11.5 }} className="num">
            {fmtInt(removedProbed)} confirmed not-serviceable (B probed there)
          </span>
          <span className="faint num" style={{ fontSize: 11.5 }}>
            {fmtInt(removedUnscanned)} inconclusive (B never probed there)
          </span>
        </div>
      )}
    </div>
  );
}

function SelectedBar({
  s,
  cat,
  verdict,
  loading,
  onClear,
}: {
  s: StoreHull;
  cat: Cat;
  verdict: Verdict | null;
  loading: boolean;
  onClear: () => void;
}) {
  const label = cat === "added" ? "added in B" : cat === "removed" ? "removed in B" : "in both runs";
  const color = CAT_COLOR[cat];
  const vTone = verdict?.tone === "crit" ? REMOVED : verdict?.tone === "warn" ? SELECTED : COMMON;
  return (
    <div
      className="col"
      style={{
        gap: 6,
        padding: "8px 10px",
        marginBottom: 8,
        border: `1px solid ${color}`,
        borderRadius: 6,
        background: "var(--panel-2, rgba(255,255,255,0.02))",
      }}
    >
      <div className="row" style={{ gap: 12, alignItems: "center" }}>
        <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>
          {s.store_id}
        </span>
        <Badge variant="outline" className="bg-card" style={{ color, borderColor: color, fontSize: 10.5 }}>
          {label}
        </Badge>
        {s.name && <span className="faint" style={{ fontSize: 12 }}>{s.name}</span>}
        <span className="faint num" style={{ fontSize: 11 }}>{s.cells} cells</span>
        <div className="flex-1" />
        <Button variant="outline" size="sm" style={{ fontSize: 11 }} onClick={onClear}>
          clear
        </Button>
      </div>
      {verdict && (
        <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
          <Badge variant="outline" className="bg-card" style={{ color: vTone, borderColor: vTone, fontSize: 10 }}>
            {verdict.tag}
          </Badge>
          <span className="faint" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
            {verdict.detail}
          </span>
        </div>
      )}
      {!verdict && loading && (
        <span className="muted" style={{ fontSize: 11 }}>checking whether the other run probed here…</span>
      )}
    </div>
  );
}

function StorePanel({
  handle,
  label,
  data,
  error,
}: {
  handle: MapHandle;
  label: string;
  data: RunStoreHulls | undefined;
  error: boolean;
}) {
  const n = data?.count ?? 0;
  return (
    <div className="col" style={{ flex: "1 1 360px", minWidth: 300, gap: 6 }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>
          {label}
        </span>
        {data && (
          <span className="num faint" style={{ fontSize: 10.5 }}>
            {fmtInt(n)} stores{data.capped ? " · capped" : ""}
          </span>
        )}
      </div>
      <div style={{ position: "relative" }}>
        <div
          ref={handle.elRef}
          style={{
            width: "100%",
            height: 480,
            borderRadius: 6,
            overflow: "hidden",
            zIndex: 0,
          }}
        />
        {error ? (
          <Overlay>Could not load store hulls.</Overlay>
        ) : n === 0 ? (
          <Overlay>No store footprints recorded for this run.</Overlay>
        ) : null}
      </div>
    </div>
  );
}

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="muted"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12.5,
        pointerEvents: "none",
        background: "rgba(0,0,0,0.04)",
        zIndex: 400,
      }}
    >
      {children}
    </div>
  );
}
